<template>
  <div class="space-y-4">
    <!-- Population picker: the page's front door -->
    <div v-if="!selectedAttribute || showPicker" class="border border-gray-200 rounded">
      <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div class="text-sm font-semibold text-gray-900">Choose a population</div>
          <div class="text-xs text-gray-500">A population is a Data Attribute: its SQL defines who is in the cohort. Pick one to profile it.</div>
        </div>
        <button v-if="selectedAttribute" @click="showPicker = false" class="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
      <div class="px-4 py-2 border-b border-gray-100">
        <input
          v-model="pickerSearch"
          type="text"
          placeholder="Search attributes..."
          class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
        />
      </div>
      <div class="max-h-[28rem] overflow-y-auto divide-y divide-gray-100">
        <div class="grid grid-cols-[minmax(0,16rem)_6rem_minmax(0,1fr)] gap-x-4 px-4 py-1.5 bg-gray-50 text-[10px] font-mono uppercase text-gray-400 sticky top-0">
          <span>Name</span><span>Type</span><span>Description</span>
        </div>
        <div
          v-for="attr in filteredAttributes"
          :key="attr.id"
          @click="selectAttribute(attr)"
          class="grid grid-cols-[minmax(0,16rem)_6rem_minmax(0,1fr)] gap-x-4 px-4 py-2 cursor-pointer hover:bg-blue-50"
        >
          <span class="text-xs text-gray-900 truncate">{{ attr.name }}</span>
          <span class="text-[10px] font-mono uppercase text-gray-400">{{ attr.attribute_type }}</span>
          <span class="text-xs text-gray-500 truncate">{{ attr.description }}</span>
        </div>
      </div>
    </div>

    <!-- Selected population -->
    <template v-if="selectedAttribute && !showPicker">
      <div class="border border-gray-200 rounded p-4 flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="text-base font-semibold text-gray-900">{{ selectedAttribute.name }}</div>
          <div class="text-xs text-gray-500 mt-0.5">{{ selectedAttribute.description }}</div>
          <div v-if="frameColumns.length" class="flex flex-wrap gap-1 mt-2">
            <code v-for="col in frameColumns" :key="col" class="text-[10px] text-gray-500">{{ col }}</code>
          </div>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <button @click="showPicker = true" class="btn-secondary">Change</button>
          <button @click="runProfile" :disabled="running" class="btn-primary">
            {{ running ? runPhase : 'Profile Population' }}
          </button>
        </div>
      </div>
      <div v-if="saveError" class="text-sm text-red-600">{{ saveError }}</div>

      <!-- The profile script -->
      <details class="border border-gray-200 rounded">
        <summary class="px-4 py-2.5 text-xs font-medium text-gray-600 cursor-pointer">
          Profile script <span class="font-normal text-gray-400">· the profile is R — edit it to extend what gets computed</span>
        </summary>
        <div class="border-t border-gray-200 flex gap-4 items-start p-3">
          <div class="flex-1 min-w-0 border border-gray-800 rounded overflow-hidden">
            <textarea
              v-model="script"
              rows="24"
              spellcheck="false"
              class="w-full px-4 py-3 bg-gray-900 text-gray-100 text-xs font-mono leading-relaxed focus:outline-none resize-y"
            ></textarea>
          </div>
          <div v-if="paramRows.length" class="w-80 shrink-0 border border-gray-200 rounded p-3 space-y-3">
            <div class="text-xs font-semibold text-gray-700">Params <span class="font-normal text-gray-400">&middot; the script's p &lt;- list(...)</span></div>
            <div v-for="param in paramRows" :key="param.name" class="flex items-center gap-2">
              <code class="text-[11px] text-gray-700 w-28 shrink-0 truncate" :title="'p$' + param.name">{{ param.name }}</code>
              <input
                :value="param.raw"
                @change="setParamValue(param.name, $event.target.value)"
                class="flex-1 min-w-0 px-1.5 py-1 border border-gray-300 rounded text-[11px] font-mono"
                title="an R literal: 5, &quot;text&quot;, TRUE"
              />
            </div>
          </div>
        </div>
      </details>

      <!-- Results: the point of the page -->
      <div v-if="results" class="border border-gray-200 rounded p-4 space-y-4">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium">Profile</span>
          <div class="flex items-center gap-2">
            <select
              v-if="profileRuns.length"
              :value="results.id"
              @change="loadRun(parseInt($event.target.value))"
              class="px-1.5 py-0.5 border border-gray-300 rounded text-[11px]"
            >
              <option v-for="(run, i) in profileRuns" :key="run.id" :value="run.id">
                #{{ profileRuns.length - i }} · {{ formatDate(run.run_at) }} · {{ run.status }}
              </option>
            </select>
            <span class="text-xs text-gray-500">{{ results.status }}</span>
          </div>
        </div>

        <div v-if="results.status === 'failed'" class="text-sm text-red-600 whitespace-pre-wrap">{{ results.error_message }}</div>
        <pre v-if="['pending', 'running'].includes(results.status)" class="text-xs bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto max-h-40">{{ results.console_output || 'queued — waiting for a worker...' }}</pre>

        <div v-if="runOutputs.length" class="flex gap-4 items-start">
          <div class="w-72 shrink-0 border border-gray-200 rounded divide-y divide-gray-100 overflow-y-auto max-h-[32rem]">
            <div
              v-for="(o, i) in runOutputs"
              :key="i"
              @click="selectedOutput = i"
              :class="[
                'px-3 py-1.5 cursor-pointer grid grid-cols-[1.2rem_4rem_minmax(0,1fr)] gap-x-2 items-baseline',
                selectedOutput === i ? 'bg-blue-50' : 'hover:bg-gray-50'
              ]"
            >
              <span class="text-[10px] font-mono text-gray-400">{{ i + 1 }}</span>
              <span class="text-[10px] font-mono uppercase text-gray-400">{{ o.kind }}</span>
              <span class="text-xs text-gray-900 truncate">{{ o.name }}</span>
            </div>
          </div>

          <div class="flex-1 min-w-0 border border-gray-200 rounded p-4" v-if="currentOutput">
            <template v-if="currentOutput.kind === 'plot'">
              <div class="text-xs font-medium text-gray-700 mb-2">{{ currentOutput.name }}</div>
              <img v-if="plotUrls[plotKey(currentOutput)]" :src="plotUrls[plotKey(currentOutput)]" :alt="currentOutput.name" class="w-full" />
              <div v-else class="text-xs text-gray-400">Loading plot…</div>
              <div v-if="currentOutput.caption" class="text-xs text-gray-400 mt-2">{{ currentOutput.caption }}</div>
            </template>

            <template v-else-if="currentOutput.kind === 'table'">
              <div class="text-xs font-medium text-gray-700 mb-2">{{ currentOutput.name }}</div>
              <div class="overflow-x-auto">
                <table class="min-w-full text-xs border border-gray-200">
                  <thead>
                    <tr class="bg-gray-50">
                      <th v-for="c in currentOutput.columns" :key="c" class="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{{ c }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in currentOutput.rows" :key="i" class="border-b border-gray-100">
                      <td v-for="c in currentOutput.columns" :key="c" class="px-2 py-1">{{ row[c] }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="currentOutput.caption" class="text-xs text-gray-400 mt-2">{{ currentOutput.caption }}</div>
            </template>

            <template v-else>
              <div class="text-xs font-medium text-gray-700 mb-1">{{ currentOutput.name }}</div>
              <p class="text-sm text-gray-700">{{ currentOutput.detail }}</p>
            </template>
          </div>
        </div>

        <details v-if="results.console_output">
          <summary class="text-xs text-gray-500 cursor-pointer">Console output</summary>
          <pre class="sensitive text-xs bg-gray-900 text-gray-100 rounded p-3 mt-1 overflow-x-auto max-h-64" data-demo-blur>{{ results.console_output }}</pre>
        </details>
      </div>


    </template>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PopulationProfiler',
  props: {
    attributes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      pickerSearch: '',
      showPicker: false,
      selectedAttribute: null,
      profileTemplate: '',
      script: '',
      profileStudyId: null,
      loadedScript: '',
      profileRuns: [],
      results: null,
      selectedOutput: 0,
      plotUrls: {},
      running: false,
      runPhase: 'Running...',
      saveError: null,
      pollTimer: null,
    };
  },
  computed: {
    filteredAttributes() {
      const q = this.pickerSearch.toLowerCase();
      const list = this.attributes.filter(
        a => !q || a.name.toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q)
      );
      return list.slice(0, 200);
    },
    frameColumns() {
      return this.selectedAttribute?.output_columns || [];
    },
    runOutputs() {
      return this.results?.results?.outputs || [];
    },
    currentOutput() {
      return this.runOutputs[this.selectedOutput] || null;
    },
    paramRows() {
      const script = this.script || '';
      const m = /\bp\s*<-\s*list\s*\(/g.exec(script);
      if (!m) return [];
      const span = this.findCallSpan(script.slice(m.index), /^p\s*<-\s*list\s*\(/);
      if (!span) return [];
      const body = script.slice(m.index + span.start, m.index + span.end);
      const rows = [];
      const re = /([\w.]+)\s*=\s*("(?:[^"\\]|\\.)*"|c\([^)]*\)|[^,\n)]+)/g;
      let am;
      while ((am = re.exec(body))) {
        rows.push({ name: am[1], raw: am[2].trim() });
      }
      return rows;
    },
    profileName() {
      return this.selectedAttribute ? `Profile: ${this.selectedAttribute.name}` : '';
    },
  },
  methods: {
    slugify(name) {
      let s = (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      if (/^\d/.test(s)) s = 'x' + s;
      return s;
    },
    findCallSpan(text, openRe) {
      const open = openRe.exec(text);
      if (!open) return null;
      let depth = 0;
      let inString = null;
      for (let i = open[0].length - 1; i < text.length; i++) {
        const ch = text[i];
        if (inString) {
          if (ch === '\\') i++;
          else if (ch === inString) inString = null;
        } else if (ch === '"' || ch === "'") {
          inString = ch;
        } else if (ch === '(') {
          depth++;
        } else if (ch === ')') {
          depth--;
          if (depth === 0) return { start: open[0].length, end: i };
        }
      }
      return null;
    },
    setParamValue(name, raw) {
      const script = this.script || '';
      const m = /\bp\s*<-\s*list\s*\(/g.exec(script);
      if (!m) return;
      const span = this.findCallSpan(script.slice(m.index), /^p\s*<-\s*list\s*\(/);
      if (!span) return;
      const bodyStart = m.index + span.start;
      const bodyEnd = m.index + span.end;
      const body = script.slice(bodyStart, bodyEnd);
      const re = new RegExp(`(${name.replace('.', '\\.')}\\s*=\\s*)("(?:[^"\\\\]|\\\\.)*"|c\\([^)]*\\)|[^,\\n)]+)`);
      const nm = re.exec(body);
      if (!nm) return;
      const newBody = body.slice(0, nm.index) + nm[1] + raw + body.slice(nm.index + nm[0].length);
      this.script = script.slice(0, bodyStart) + newBody + script.slice(bodyEnd);
    },
    async selectAttribute(attr) {
      this.selectedAttribute = attr;
      this.showPicker = false;
      this.results = null;
      this.profileRuns = [];
      this.profileStudyId = null;
      this.saveError = null;

      try {
        const listResp = await axios.get('api/impact_analytics/studies/', { params: { kind: 'profile' } });
        const existing = listResp.data.find(s => s.name === this.profileName);
        if (existing) {
          this.profileStudyId = existing.id;
          const detail = await axios.get(`api/impact_analytics/studies/${existing.id}/`);
          this.script = detail.data.script;
          this.loadedScript = detail.data.script;
          this.fetchRuns(existing.id, true);
          return;
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
      this.script = this.renderTemplate(attr);
      this.loadedScript = '';
    },
    renderTemplate(attr) {
      const slug = this.slugify(attr.name);
      return (this.profileTemplate || '').replace(/ks_frame\("[^"]*"\)/, `ks_frame("${slug}")`);
    },
    async saveProfile() {
      const payload = {
        name: this.profileName,
        script: this.script,
        script_language: 'r',
        template_key: 'population_profile',
      };
      try {
        let response;
        if (this.profileStudyId) {
          response = await axios.put(`api/impact_analytics/studies/${this.profileStudyId}/`, payload);
        } else {
          response = await axios.post('api/impact_analytics/studies/', payload);
        }
        this.profileStudyId = response.data.id;
        return true;
      } catch (error) {
        this.saveError = error.response?.data?.error || JSON.stringify(error.response?.data || error.message);
        return false;
      }
    },
    async runProfile() {
      if (this.running) return;
      this.running = true;
      this.runPhase = 'Saving...';
      this.saveError = null;
      this.results = null;

      if (this.profileStudyId && this.script === this.loadedScript) {
        // Not edited locally: adopt the server's current script instead of
        // overwriting it with a possibly stale browser copy.
        try {
          const detail = await axios.get(`api/impact_analytics/studies/${this.profileStudyId}/`);
          this.script = detail.data.script;
          this.loadedScript = detail.data.script;
        } catch (error) {
          console.error('Error refreshing profile script:', error);
        }
      } else {
        const saved = await this.saveProfile();
        if (!saved) {
          this.running = false;
          return;
        }
        this.loadedScript = this.script;
      }

      this.runPhase = 'Profiling...';
      try {
        const response = await axios.post(`api/impact_analytics/studies/${this.profileStudyId}/run/`, {});
        this.results = response.data;
        this.selectedOutput = 0;
        if (['pending', 'running'].includes(response.data.status)) {
          this.pollRun(response.data.id);
        } else {
          this.running = false;
        }
      } catch (error) {
        this.saveError = error.response?.data?.error || error.message;
        this.running = false;
      }
    },
    pollRun(runId) {
      this.pollTimer = setInterval(async () => {
        try {
          const response = await axios.get(`api/impact_analytics/study-runs/${runId}/`);
          this.results = response.data;
          if (this.selectedOutput >= (response.data.results?.outputs || []).length) {
            this.selectedOutput = 0;
          }
          if (['completed', 'failed'].includes(response.data.status)) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.running = false;
            this.fetchPlots();
            if (this.profileStudyId) this.fetchRuns(this.profileStudyId);
          }
        } catch (error) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
          this.running = false;
        }
      }, 2500);
    },
    async fetchRuns(studyId, autoloadLatest = false) {
      try {
        const response = await axios.get(`api/impact_analytics/studies/${studyId}/runs/`);
        this.profileRuns = response.data;
        if (autoloadLatest && this.profileRuns.length) {
          this.loadRun(this.profileRuns[0].id);
        }
      } catch (error) {
        console.error('Error fetching profile runs:', error);
      }
    },
    async loadRun(runId) {
      try {
        const response = await axios.get(`api/impact_analytics/study-runs/${runId}/`);
        this.results = response.data;
        this.selectedOutput = 0;
        this.fetchPlots();
      } catch (error) {
        console.error('Error loading run:', error);
      }
    },
    plotKey(output) {
      return `${this.results && this.results.id}:${output.file}`;
    },
    async fetchPlots() {
      if (!this.results || !this.results.results) return;
      const outputs = this.results.results.outputs || [];
      for (const output of outputs.filter((o) => o.kind === 'plot')) {
        const key = this.plotKey(output);
        if (this.plotUrls[key]) continue;
        const slug = (output.file || '').replace(/^plots\//, '').replace(/\.png$/, '');
        try {
          const response = await axios.get(
            `api/impact_analytics/study-runs/${this.results.id}/artifact/`,
            { params: { name: slug }, responseType: 'blob' }
          );
          this.plotUrls[key] = URL.createObjectURL(response.data);
        } catch (error) {
          console.error('Error fetching plot:', slug, error);
        }
      }
    },
    formatDate(isoString) {
      if (!isoString) return '';
      return new Date(isoString).toLocaleString();
    },
    async fetchTemplate() {
      try {
        const response = await axios.get('api/impact_analytics/study-templates/');
        const t = response.data.find(x => x.key === 'population_profile');
        this.profileTemplate = t ? t.script : '';
      } catch (error) {
        console.error('Error fetching profile template:', error);
      }
    },
  },
  mounted() {
    this.fetchTemplate();
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
};
</script>
